import {
    ApiChatAttachmentKind,
    ApiChatReadState,
    type AppChatActivityResponse,
    type ChatDialogResponse,
    type ChatDialogMessageResponse,
} from '@/core/generated/api';
import { getMoscowDayKey, formatMoscowCalendarDay } from '@/shared/lib/dateTime';
import { getChatDialogDisplayName } from '@/entities/ChatDialog/model/selectors';
import type { UserChatActivity, UserChatProfile } from './model/types';
import type { UserChatSummary, ChatMessage, ChatMessageGroup } from '@/entities/Chat/model/types';

/** Возвращает имя участника переписки для списка и заголовка */
export const getUserChatProfileName = (selection: UserChatProfile): string => (
    selection.role === 'Client'
        ? selection.profile.firstName || 'Клиент без имени'
        : [selection.profile.name, selection.profile.lastName].filter(Boolean).join(' ') || 'Исполнитель без имени'
);

/** Подсчитывает диалоги и находит последнее сообщение пользователя */
export const mapUserChatActivity = (source: UserChatSummary[]): UserChatActivity => {
    const chats = [...new Map(source.map((chat) => [chat.id, chat])).values()];

    const latest = chats.reduce<UserChatSummary | null>((current, chat) => (
        (chat.hasMessages ?? Boolean(chat.lastMessage.trim())) && chat.lastMessageAt !== null
            && (current?.lastMessageAt == null || chat.lastMessageAt > current.lastMessageAt)
            ? chat
            : current
    ), null);

    return {
        dialogCount: chats.length,
        lastMessageAt: latest?.lastMessageAt ?? null,
        lastMessage: latest?.lastMessage ?? '',
    };
};

/** Ставит пользователей со свежими сообщениями в начало списка */
export const sortUserChatProfiles = (
    profiles: UserChatProfile[],
    activity: Record<string, UserChatActivity | null>,
): UserChatProfile[] => [...profiles].sort((left, right) => {
    const leftTime = activity[left.profile.appUserId ?? '']?.lastMessageAt ?? Number.NEGATIVE_INFINITY;
    const rightTime = activity[right.profile.appUserId ?? '']?.lastMessageAt ?? Number.NEGATIVE_INFINITY;

    return (leftTime === rightTime
        ? 0
        : leftTime > rightTime
            ? -1
            : 1)
        || getUserChatProfileName(left).localeCompare(getUserChatProfileName(right), 'ru')
        || (left.profile.id ?? '').localeCompare(right.profile.id ?? '');
});

/** Преобразует активность приложения в индекс списка пользователей */
export const mapAppChatActivity = (
    source: AppChatActivityResponse[]
): Record<string, UserChatActivity> => {
    const result: Record<string, UserChatActivity> = {};

    for (const item of source) {
        if (!item.appUserId) continue;
        result[item.appUserId] = {
            dialogCount: item.dialogCount ?? 0,
            lastMessageAt: item.lastActivityAt
                ? Date.parse(item.lastActivityAt)
                : null,
            lastMessage: '',
        };
    }
    return result;
};


/** Преобразует диалог Supabase для прежнего списка переписок пользователя */
export const appDialogToUserChatSummary = (dialog: ChatDialogResponse): UserChatSummary => ({
    id: dialog.id ?? '',
    companion: {
        id: dialog.companionAppUserId ?? dialog.companionId ?? '',
        name: getChatDialogDisplayName(dialog),
        role: dialog.companionRole?.id ?? '',
        displayNameRole: dialog.companionRole?.name ?? 'Собеседник',
        photoUrl: dialog.companionAvatarUrl ?? null,
    },
    lastMessage: dialog.lastMessage?.text ?? '',
    lastMessageAt: dialog.lastActivityAt
        ? Date.parse(dialog.lastActivityAt)
        : null,
    isViewed: (dialog.unreadCount ?? 0) === 0,
    unreadCount: dialog.unreadCount ?? 0,
    hasMessages: Boolean(dialog.lastMessage),
});

/** Преобразует сообщение Supabase для прежнего отображения в режиме просмотра */
export const appMessageToUserChatMessage = (message: ChatDialogMessageResponse): ChatMessage => ({
    id: message.id ?? '',
    message: message.text ?? '',
    photoUrls: [...(message.attachments ?? [])]
        .sort((left, right) => (left.position ?? 0) - (right.position ?? 0))
        .filter((attachment) => attachment.kind?.id === ApiChatAttachmentKind.Image && attachment.url)
        .map((attachment) => attachment.url ?? ''),
    isMine: message.isOutgoing ?? false,
    isViewed: message.readState?.id === ApiChatReadState.Read,
    createdAt: Date.parse(message.sentAt ?? message.receivedAt ?? ''),
});

/** Разделяет историю пользователя по московским календарным дням */
export const groupUserChatMessages = (messages: ChatMessage[]): ChatMessageGroup[] => {
    const groups = new Map<string, ChatMessageGroup>();

    for (const message of messages) {
        const key = getMoscowDayKey(message.createdAt);
        let group = groups.get(key);

        if (!group) {
            group = {
                key,
                label: formatMoscowCalendarDay(message.createdAt),
                messages: []
            };
            groups.set(key, group);
        }

        group.messages.push(message);
    }

    return [...groups.values()];
};
